import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

import { MID, TINY, marketingPalette } from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import {
  LandingButton,
  LandingImage,
  LandingSection,
} from "../LandingPrimitives";
import { PRICED_STACK, PricedHeading } from "./cardsPanel";
import { sectionPad } from "./landingRun";

type PlanGroups = Laid<"planGroups">;
type Group = PlanGroups["groups"][number];
type Plan = Group["plans"][number];

/**
 * `.premium-dc_wrap` and `.premium-res_wrap`: the published pair sets the
 * datacenter group on green and the residential one on blue, and each plan's
 * quantity chip takes its group's tint.
 */
const TONES = ["#E6F4EA", "#E6F1F7"] as const;

/** `.premium-plan_wrap`: two groups to a row, stacked below the card step. */
const GROUPS = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "16px",
  width: 1,
  [MID]: { display: "flex", flexDirection: "column" },
} satisfies SxProps<Theme>;

/** `.premium-dc_wrap`: a tinted group, 24 in, 16 on a phone. */
const GROUP = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  p: "24px",
  borderRadius: "12px",
  [TINY]: { p: "16px" },
} satisfies SxProps<Theme>;

/** `.premium-grid_wrap`: as many 16rem plans as the group fits. */
const PLANS = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 16rem), 1fr))",
  gap: "16px",
} satisfies SxProps<Theme>;

/** `.premium-item`. */
const PLAN = {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  p: "20px",
  borderRadius: "12px",
  backgroundColor: "common.white",
  boxShadow: "0 4px 12px -6px #ABD4B7",
  [TINY]: { p: "14.4px" },
} satisfies SxProps<Theme>;

/** `.premium-plan_top`: a mark beside a title and the line under it. */
function Heading({
  icon,
  size,
  title,
  subtitle,
  titleSx,
  subtitleSx,
}: {
  icon?: string;
  size: string;
  title: string;
  subtitle?: string;
  titleSx: SxProps<Theme>;
  subtitleSx: SxProps<Theme>;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
      {icon ? (
        <LandingImage src={icon} sx={{ width: size, height: size }} />
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          gap: "2px",
        }}
      >
        <Box sx={titleSx}>{title}</Box>
        {subtitle ? <Box sx={subtitleSx}>{subtitle}</Box> : null}
      </Box>
    </Box>
  );
}

/** `.premium-item_middle`: the quantity chip, the unit price, and the total it bills. */
function PlanPrice({ plan, tone }: { plan: Plan; tone: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "16px",
      }}
    >
      {plan.quantity ? (
        <Box
          sx={{
            px: "8px",
            py: "4px",
            borderRadius: "4px",
            backgroundColor: tone,
            color: marketingPalette.ink,
            fontSize: 15,
            fontWeight: 500,
          }}
        >
          {plan.quantity}
        </Box>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "4px" }}>
        <Box
          sx={{
            color: marketingPalette.ink,
            fontSize: 34,
            fontWeight: 500,
            lineHeight: "36px",
          }}
        >
          {plan.price}
        </Box>
        {plan.per ? <Box sx={{ lineHeight: "28px" }}>{plan.per}</Box> : null}
      </Box>
      {plan.total ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Box
            sx={{
              color: marketingPalette.ink,
              fontSize: 16,
              fontWeight: 500,
              lineHeight: "20px",
            }}
          >
            {plan.total}
          </Box>
          {plan.note ? (
            <Box
              sx={{
                color: "rgba(79, 92, 101, 0.7)",
                fontSize: 12,
                lineHeight: "20px",
              }}
            >
              {plan.note}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function PlanCard({ plan, tone }: { plan: Plan; tone: string }) {
  return (
    <Box sx={PLAN}>
      <Heading
        icon={plan.icon}
        size="40px"
        title={plan.title}
        subtitle={plan.subtitle}
        titleSx={{
          color: marketingPalette.ink,
          fontSize: 19,
          fontWeight: 500,
          lineHeight: 1.2,
        }}
        subtitleSx={{
          color: marketingPalette.ink,
          opacity: 0.56,
          fontSize: 15,
        }}
      />
      <PlanPrice plan={plan} tone={tone} />
      {/* `.button_new.w-button`: published as its label alone, no arrow. */}
      {plan.cta ? (
        <LandingButton cta={plan.cta} variant="primary" arrow={false} />
      ) : null}
    </Box>
  );
}

/**
 * `.section_title-pr.is-hero-rpoduct-card` over `.premium-plan_wrap`: the
 * plans a kind of proxy comes in, a tinted group per kind under its own mark.
 */
export default function PlanGroupsBlock({ block }: { block: PlanGroups }) {
  return (
    <LandingSection padding={block.pad ? sectionPad(block.pad) : "none"}>
      <Box sx={PRICED_STACK}>
        <PricedHeading title={block.title} body={block.body} />
        <Box sx={GROUPS}>
          {block.groups.map((group, index) => {
            const tone = TONES[index % TONES.length];
            return (
              <Box key={index} sx={[GROUP, { backgroundColor: tone }]}>
                <Heading
                  icon={group.icon}
                  size="32px"
                  title={group.title}
                  subtitle={group.subtitle}
                  titleSx={{
                    color: marketingPalette.ink,
                    textTransform: "uppercase",
                    fontSize: 17,
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                  subtitleSx={{ textAlign: "left", fontSize: 15 }}
                />
                <Box sx={PLANS}>
                  {group.plans.map((plan, planIndex) => (
                    <PlanCard key={planIndex} plan={plan} tone={tone} />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </LandingSection>
  );
}
